import type { ConsentState } from '@redbrickmedia/duplex.user-identity'
import type { Mixpanel, People } from 'mixpanel-browser'

type MixpanelIdentityApi = Pick<
  Mixpanel,
  'register' | 'unregister' | 'identify' | 'reset' | 'get_distinct_id'
> & { people: Pick<People, 'set' | 'set_once'> }

export function withoutTargetingKey(
  metadata: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(metadata).filter(([key]) => key !== 'targetingKey')
  )
}

/**
 * Attach the targeting key to Mixpanel only on allow. Otherwise drop the
 * persisted super property and any persisted `distinct_id` that is the
 * targeting key, so the next events go out anonymous until allow re-identifies.
 */
export function applyIdentifierConsent(
  mixpanel: MixpanelIdentityApi,
  state: ConsentState,
  metadata: Record<string, unknown>
): void {
  const targetingKey =
    typeof metadata.targetingKey === 'string' ? metadata.targetingKey : ''

  if (state === 'allow') {
    if (!targetingKey) return
    mixpanel.register({ targetingKey })

    // Cohort goes on the Mixpanel People profile for reporting
    const cohort = metadata.cohort ? String(metadata.cohort) : ''
    if (!cohort) return
    mixpanel.identify(targetingKey)
    if (mixpanel.people.set_once) {
      mixpanel.people.set_once({ cohort })
    } else {
      mixpanel.people?.set({ cohort })
    }
    return
  }

  mixpanel.unregister('targetingKey')
  if (targetingKey && mixpanel.get_distinct_id() === targetingKey) {
    mixpanel.reset()
    mixpanel.register(withoutTargetingKey(metadata))
  }
}
